/**
 * CHAY THU MOT LAN SUA TRONG LUC NGUOI DUNG DANG GO.
 *
 * Vi sao phai co: mot lan sua co the bi tu choi vi sau ly do khac nhau (thieu
 * glyph, khong vua khoi, trang xoay, mau trung nen...). Neu chi kiem tra luc
 * bam xac nhan thi nguoi dung go xong ca doan roi moi biet la khong duoc. Ho se
 * khong hieu ly do, va se doan.
 *
 * Vi sao phai gian nhip (debounce): moi lan chay thu deu trich lai toan bo khoi
 * van ban cua trang - thao tac dat nhat trong ca vong doi mot trang. Goi no
 * theo tung phim go se lam treo o soan tren tai lieu day chu.
 *
 * Vi sao co the dem luot (token): cac lan chay thu ve khong theo thu tu goi.
 * Khong co no thi ket qua cua "Xin ch" co the ve SAU ket qua cua "Xin chao" va
 * ghi de len - giao dien se bao thieu glyph cho mot chuoi khong con ton tai.
 */
import { useEffect, useRef, useState } from 'react';
import type { DocumentId, PdfCore } from '@pdfeditor/types';
import { asTextEditCore, type TextAlign, type TextReplaceReport } from './textEditCore';

/** Do tre truoc khi chay thu, mili giay. Du lau de go tron mot tu ngan. */
export const PREVIEW_DEBOUNCE_MS = 260;

export interface PreviewState {
  report: TextReplaceReport | null;
  /**
   * Chuoi ma `report` duoc tinh CHO. null khi chua co report nao.
   *
   * Ly do ton tai: effect chay thu chi khoi dong SAU khi React ve xong, nen co
   * dung mot khung hinh ma `text` da la ban moi con `report` van la ban cu voi
   * pending=false. Trong khung hinh do nut xac nhan sang len kem ket qua chay
   * thu cua chuoi TRUOC DO - go mot chuoi qua dai roi bam ngay se lot qua.
   * Doi chieu forText voi chuoi hien tai la cach duy nhat chac chan; dua vao
   * rieng co `pending` la con dua vao thu tu ve cua React.
   */
  forText: string | null;
  /**
   * Can le ma `report` duoc tinh CHO. null khi lan chay thu do khong chi dinh
   * can le (loi tu suy ra).
   *
   * Cung mot ly do voi `forText`, chi khac truc: doi can le cung lam bao cao cu
   * het gia tri: so dong khong doi nhung TOA DO X cua tung dong thi doi, va bao
   * cao la thu ban xem truoc doc de ve. Khong doi chieu truong nay thi co dung
   * mot khung hinh ban xem truoc ve can giua trong khi bao cao van la cua can
   * trai - lai la ban xem truoc noi doi, chi la mot ly do khac.
   */
  forAlign: TextAlign | null;
  /**
   * DAM / NGHIENG ma `report` duoc tinh CHO.
   *
   * Cung mot cua so tuoi moi voi `forText` va `forAlign`, va no la BAT BUOC
   * chu khong phai cho ky luong: font dam RONG HON font thuong, nen mot doan
   * vua khoi khi de thuong co the khong vua khi in dam. Bao cao "vừa khối" cua
   * mat chu thuong dem ap cho mat chu dam la dung kieu "thanh cong gia" ma ca
   * tep applyTextReplace.ts duoc viet ra de chan - chi doi truc mot lan nua.
   */
  forBold: boolean;
  forItalic: boolean;
  /**
   * HO FONT ma `report` duoc tinh CHO. null khi lan chay thu do khong chi dinh
   * phong (de phong mac dinh).
   *
   * Cung cua so tuoi moi voi `forBold` / `forItalic`, va cung BAT BUOC: moi ho
   * font co so do rieng - be rong glyph khac, dau tieng Viet chong tang o do cao
   * khac (bai hoc so 4). Mot doan "vua khối" o Noto Sans co the KHONG vua o Noto
   * Serif. Cho phep xac nhan bang bao cao tinh cho phong A trong khi nguoi dung
   * da doi sang phong B la dung che do "thanh cong gia" ma ca duong sua chu nay
   * duoc viet ra de chan.
   */
  forFamily: string | null;
  /**
   * CO CHU ma `report` duoc tinh CHO. null khi lan chay thu do khong chi dinh
   * co chu (giu co chu do duoc tu chu cu).
   *
   * Cung cua so tuoi moi voi `forFamily`: doi co chu doi so dong va toa do dat
   * chu (ADR-005: co chu lon co the bi thu nho de vua khoi). Bao cao tinh cho co
   * chu A khong duoc phep xac nhan sau khi nguoi dung doi sang co chu B.
   */
  forSize: number | null;
  /** Dang cho ket qua - giao dien phai KHOA nut xac nhan trong luc nay. */
  pending: boolean;
  /**
   * Loi khong co kha nang chay thu (vd loi WASM chua hien thuc previewTextReplace).
   * Khac han voi "chay thu xong va bi tu choi": o day ta KHONG BIET, va giao dien
   * phai noi la khong biet chu khong duoc doan la duoc.
   */
  unsupported: boolean;
}

const IDLE: PreviewState = {
  report: null,
  forText: null,
  forAlign: null,
  forBold: false,
  forItalic: false,
  forFamily: null,
  forSize: null,
  pending: false,
  unsupported: false,
};

/** Kieu chu cho mot lan chay thu. Bo trong = mat chu thuong, phong mac dinh. */
export interface PreviewStyle {
  /** Bo qua = de loi suy ra can le tu chu cu. */
  align?: TextAlign | undefined;
  bold?: boolean | undefined;
  italic?: boolean | undefined;
  /** Ma ho font. Bo qua = phong mac dinh (noto-sans). */
  family?: string | undefined;
  /** Co chu, diem. Bo qua = giu co chu do duoc tu chu cu. */
  fontSizePt?: number | undefined;
}

export function useTextEditPreview(
  core: PdfCore | null,
  doc: DocumentId | null,
  blockId: string | null,
  text: string,
  style: PreviewStyle = {},
): PreviewState {
  const align = style.align;
  const bold = style.bold === true;
  const italic = style.italic === true;
  const family = style.family;
  const size = style.fontSizePt;

  const [state, setState] = useState<PreviewState>(IDLE);
  const tokenRef = useRef(0);

  useEffect(() => {
    if (!core || !doc || !blockId) {
      setState(IDLE);
      return;
    }
    const editCore = asTextEditCore(core);
    if (!editCore) {
      setState({
        report: null,
        forText: null,
        forAlign: null,
        forBold: false,
        forItalic: false,
        forFamily: null,
        forSize: null,
        pending: false,
        unsupported: true,
      });
      return;
    }

    const token = ++tokenRef.current;
    setState((prev) => ({
      report: prev.report,
      forText: prev.forText,
      forAlign: prev.forAlign,
      forBold: prev.forBold,
      forItalic: prev.forItalic,
      forFamily: prev.forFamily,
      forSize: prev.forSize,
      pending: true,
      unsupported: false,
    }));

    const timer = setTimeout(() => {
      void editCore
        .previewTextReplace(doc, blockId, text, {
          ...(align ? { align } : {}),
          ...(bold ? { bold: true } : {}),
          ...(italic ? { italic: true } : {}),
          ...(family ? { fontFamily: family } : {}),
          ...(size !== undefined ? { fontSizePt: size } : {}),
        })
        .then((result) => {
          // Ket qua cu ve muon: bo di. Xem ghi chu ve dem luot o dau tep.
          if (token !== tokenRef.current) return;
          if (result.ok) {
            setState({
              report: result.value,
              forText: text,
              forAlign: align ?? null,
              forBold: bold,
              forItalic: italic,
              forFamily: family ?? null,
              forSize: size ?? null,
              pending: false,
              unsupported: false,
            });
          } else {
            // Loi tu choi o tang tren chay thu (vd khong tim thay khoi). Dung
            // chinh thong bao tieng Viet cua loi, khong bia lai.
            setState({
              report: {
                ...FAILED_SHELL,
                reason: result.error.userMessage,
              },
              forText: text,
              forAlign: align ?? null,
              forBold: bold,
              forItalic: italic,
              forFamily: family ?? null,
              forSize: size ?? null,
              pending: false,
              unsupported: false,
            });
          }
        })
        .catch((err: unknown) => {
          if (token !== tokenRef.current) return;
          setState({
            report: {
              ...FAILED_SHELL,
              reason: `Không chạy thử được lần sửa này: ${String(err).slice(0, 160)}`,
            },
            forText: text,
            forAlign: align ?? null,
            forBold: bold,
            forItalic: italic,
            forFamily: family ?? null,
            forSize: size ?? null,
            pending: false,
            unsupported: false,
          });
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [core, doc, blockId, text, align, bold, italic, family, size]);

  return state;
}

/**
 * Vo bao cao cho cac that bai KHONG den tu preflight (loi mang, khoi bien mat).
 * Moi truong so deu null: ta khong do duoc gi, va so 0 se noi doi la da do.
 */
const FAILED_SHELL: TextReplaceReport = {
  ok: false,
  outcome: 'tu-choi-font',
  mode: 'replace',
  errorCode: 'unsupported-feature',
  reason: '',
  tier: 'none',
  tierLabel: 'không-phân-giải-được',
  substituted: false,
  fontReason: '',
  fsType: null,
  fsTypeTrusted: false,
  align: 'left',
  alignDetected: 'left',
  lineCount: 0,
  shrinkRatio: null,
  fontSizePt: null,
  neededHeightPt: null,
  widestLinePt: null,
  colorDistance: null,
  pageRotation: null,
  newFontBaseName: null,
  coverRect: null,
  bytes: null,
};
